import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, ArrowRight, Truck } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { TwoFactorStep } from "@/components/TwoFactorStep";

export default function Login() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [challenge, setChallenge] = useState(null);

    // Route drivers + helpers straight to /driver so they never see a
    // dashboard they don't use. Everyone else lands on the operations
    // console. Reused for both the "already logged in" redirect and the
    // fresh-submit redirect below.
    const homeFor = (u) => (u?.role === "driver" || u?.role === "driver_helper" ? "/driver" : "/");

    useEffect(() => { if (user) nav(homeFor(user), { replace: true }); }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const u = await login(email, password);
            if (u.requires_2fa) { setChallenge(u.challenge_token); return; }
            nav(homeFor(u), { replace: true });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex relative overflow-hidden bg-foreground text-background">
                <div className="absolute inset-0 grid-noise opacity-[0.04]" />
                <img
                    src="https://images.unsplash.com/photo-1762235634143-6d350fe349e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                    alt="WasteHQ truck"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/10" />
                <div className="relative z-10 p-12 flex flex-col justify-between w-full">
                    <div className="flex items-center gap-3">
                        <div className="bg-white rounded-lg px-4 py-2.5">
                            <img src="/wastehq-logo.webp" alt="WasteHQ" className="h-10 w-auto" data-testid="login-brand-logo" />
                        </div>
                    </div>
                    <div>
                        <div className="overline opacity-70 mb-3">The operating system for modern waste haulers</div>
                        <h1 className="font-heading text-5xl font-bold tracking-tighter leading-[1.05] max-w-md">
                            Less office work. More clean streets.
                        </h1>
                        <p className="mt-4 text-sm opacity-70 max-w-md leading-relaxed">
                            One platform for routing, dispatch, billing, fleet and customer service —
                            built for the way your team actually works.
                        </p>
                    </div>
                    <div className="grid grid-cols-3 gap-6 text-xs">
                        {[
                            ["Hauls today", "284"],
                            ["Routes active", "12"],
                            ["On-time %", "98.4"],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <div className="font-heading text-3xl font-bold tabular">{v}</div>
                                <div className="overline opacity-70 mt-1">{k}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center px-6 py-12">
                <Card className="w-full max-w-md border-border">
                    <CardContent className="p-8">
                        <div className="flex lg:hidden items-center gap-3 mb-8">
                            <img src="/wastehq-logo.webp" alt="WasteHQ" className="h-9 w-auto" />
                        </div>
                        {challenge ? (
                            <TwoFactorStep challenge={challenge}
                                           onDone={(u) => nav(homeFor(u), { replace: true })}
                                           onRestart={() => { setChallenge(null); setPassword(""); }} />
                        ) : (<>
                        <div className="overline mb-2">Sign in</div>
                        <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Welcome back.</h2>
                        <p className="text-sm text-muted-foreground mb-8">
                            Use your office staff credentials to access the operations console.
                        </p>

                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-xs font-medium">Email</Label>
                                <Input id="email" type="email" value={email} required
                                       onChange={(e) => setEmail(e.target.value)}
                                       data-testid="login-email-input" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-xs font-medium">Password</Label>
                                <Input id="password" type="password" value={password} required
                                       onChange={(e) => setPassword(e.target.value)}
                                       data-testid="login-password-input" />
                            </div>
                            {err && (
                                <div data-testid="login-error" className="text-xs text-red-600 dark:text-red-400 p-2 bg-red-500/10 border border-red-500/20 rounded-md">
                                    {err}
                                </div>
                            )}
                            <Button type="submit" className="w-full" disabled={busy}
                                    data-testid="login-submit-btn">
                                {busy ? "Signing in…" : "Sign in"}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </form>
                        <div className="mt-6 pt-6 border-t border-border text-xs text-muted-foreground space-y-1">
                            <div className="pt-2">
                                Customer?{" "}
                                <Link to="/portal/login" className="text-primary hover:underline" data-testid="portal-link">
                                    Use the customer portal →
                                </Link>
                            </div>
                            <div>
                                New customer?{" "}
                                <Link to="/signup" className="text-primary hover:underline" data-testid="signup-link">
                                    Sign up for service →
                                </Link>
                            </div>
                        </div>
                        </>)}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
